import { useProfileData } from '../AuthProvider'
import Logo from '../Logo'
import * as style from './style.module.scss'
import { isProductionAllowedLoginEmail } from '../../utils/productionLogin'
import { Link } from 'gatsby'
import * as React from 'react'
import { createPortal } from 'react-dom'

const RELEASE_SHA = (process.env.GATSBY_COMMIT_SHA || 'dev').slice(0, 6)
const RELEASE_HASH_COLOR = /^[0-9a-fA-F]{6}$/.test(RELEASE_SHA)
  ? `#${RELEASE_SHA}`
  : undefined

function ReleaseLabel({ email }) {
  const canTriggerCron = isProductionAllowedLoginEmail(email)
  const [open, setOpen] = React.useState(false)
  const [cronStatus, setCronStatus] = React.useState(null)
  const anchorRef = React.useRef(null)
  const tooltipIdRef = React.useRef(null)
  if (!tooltipIdRef.current) {
    tooltipIdRef.current = `footer-release-${Math.random().toString(36).slice(2, 9)}`
  }
  const tooltipId = tooltipIdRef.current
  const [coords, setCoords] = React.useState({ top: 0, left: 0 })

  const reposition = React.useCallback(() => {
    const el = anchorRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    setCoords({
      top: rect.top - 6,
      left: rect.left + rect.width / 2,
    })
  }, [])

  const show = React.useCallback(() => {
    reposition()
    setOpen(true)
  }, [reposition])

  const hide = React.useCallback(() => setOpen(false), [])

  const runBalanceCron = React.useCallback(async () => {
    if (!canTriggerCron) return

    const token =
      typeof window !== 'undefined' ? window.localStorage.getItem('doggos_l') : null
    if (!token) return

    setCronStatus('Running…')
    setOpen(true)

    try {
      const response = await fetch('/api/dev/charge-balances?force=1', {
        headers: { Authorization: `Bearer ${token}` },
      })
      const body = await response.json()
      if (!response.ok) {
        setCronStatus(body?.error || 'Failed')
        return
      }
      if (!body.results?.length) {
        setCronStatus('No eligible balances')
        return
      }
      setCronStatus(
        body.results
          .map((row) =>
            row.ok ? `${row.code} charged` : `${row.code}: ${row.error || 'failed'}`
          )
          .join(', ')
      )
    } catch (cause) {
      setCronStatus(cause?.message || 'Failed')
    } finally {
      window.setTimeout(() => setCronStatus(null), 6000)
    }
  }, [canTriggerCron])

  const handleClick = React.useCallback(() => {
    if (canTriggerCron) {
      runBalanceCron()
    }
  }, [canTriggerCron, runBalanceCron])

  React.useEffect(() => {
    if (!open) return undefined
    reposition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, reposition])

  const tooltip =
    open && typeof document !== 'undefined'
      ? createPortal(
          <span
            id={tooltipId}
            role='tooltip'
            className={style.releaseTooltip}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              transform: 'translate(-50%, -100%)',
            }}
            onMouseEnter={show}
            onMouseLeave={hide}
          >
            <span
              className={style.releasePopoverHash}
              style={RELEASE_HASH_COLOR ? { color: RELEASE_HASH_COLOR } : undefined}
            >
              #{RELEASE_SHA}
            </span>{' '}
            {cronStatus || 'Current release'}
          </span>,
          document.body
        )
      : null

  return (
    <p className={style.release}>
      <span
        ref={anchorRef}
        className={style.releaseTrigger}
        tabIndex={0}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={handleClick}
      >
        #{RELEASE_SHA}
      </span>
      {tooltip}
    </p>
  )
}

function FooterLegal({ email }) {
  return (
    <div className={style.legalText}>
      <p>
        <a
          className={style.trademark}
          href='https://tsdr.uspto.gov/#caseNumber=97266791&caseSearchType=US_APPLICATION&caseType=DEFAULT&searchType=statusSearch'
        >
          ™
        </a>{' '}
        © 2017-2026
      </p>
      <ReleaseLabel email={email} />
    </div>
  )
}

function Footer() {
  const profile = useProfileData({ optional: true })
  const [isClient, setIsClient] = React.useState(false)

  React.useEffect(() => {
    setIsClient(true)
  }, [])

  return (
    <>
      {isClient && (
        <>
          {profile ? (
            <div className={style.footerContainer}>
              <div className={style.footerContent}>
                <div className={style.footerCompanyDetails}>
                  <div className={style.logoContainer}>
                    <Logo />
                    <FooterLegal email={profile?.email} />
                  </div>
                  <div className={style.linkContainer}>
                    <Link to='/terms'>Terms</Link>
                    <Link to='/privacy'>Privacy</Link>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={style.footerContainer}>
              <div className={style.footerContent}>
                <div className={style.footerLinks}>
                  <div>
                    <h4>Cities</h4>
                    <ul>
                      <li>
                        <p>
                          <Link to='/search/?indices%5BListings%5D%5Bquery%5D=las%20vegas/'>
                            Las Vegas
                          </Link>
                        </p>
                      </li>
                      <li>
                        <p>
                          <Link to='/search/?indices%5BListings%5D%5Bquery%5D=north%20las%20vegas/'>
                            North Las Vegas
                          </Link>
                        </p>
                      </li>
                      <li>
                        <p>
                          <Link to='/search/?indices%5BListings%5D%5Bquery%5D=henderson/'>
                            Henderson
                          </Link>
                          <span className={style.betaBadge}>Beta</span>
                        </p>
                      </li>
                      <li>
                        <p>
                          Phoenix <span>(Coming soon)</span>
                        </p>
                      </li>
                    </ul>
                  </div>
                  <div className={style.companyLinks}>
                    <ul>
                      <li>
                        <Link
                          className={style.sitterLink}
                          to='/become-a-sitter/'
                        >
                          Become a sitter
                        </Link>
                      </li>
                      <li>
                        <p>
                          <Link to='/blog-posts/'>Resources</Link>
                        </p>
                      </li>
                      <li>
                        <p>
                          <Link to='/careers/'>Careers</Link>
                        </p>
                      </li>
                      <li>
                        <p>
                          <Link to='/about/'>About</Link>
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className={style.footerCompanyDetails}>
                  <div className={style.logoContainer}>
                    <Logo />
                    <FooterLegal email={profile?.email} />
                  </div>
                  <div className={style.linkContainer}>
                    <Link to='/terms'>Terms</Link>
                    <Link to='/privacy'>Privacy</Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}

export default Footer
